/**
 * Authentication Context
 * Provides auth state and methods throughout the app.
 *
 * The token portion of state is driven by lib/auth-token's subscribe API,
 * so consumers stay in sync when apiClient performs an internal refresh
 * (e.g. on a 401) without re-rendering this provider explicitly.
 */

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User, AuthState, LoginCredentials, AuthTokens } from '@/types/auth';
import { apiClient } from '@/lib/api-client';
import { getStoredTokens, subscribeToTokens } from '@/lib/auth-token';
import { getTokenExpiration, getUserFromIdToken, isTokenExpired } from '@/lib/auth-utils';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function deriveUser(tokens: AuthTokens | null): User | null {
  if (!tokens?.idToken) return null;
  try {
    return getUserFromIdToken(tokens.idToken);
  } catch (error) {
    console.error('Failed to decode ID token:', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokensState] = useState<AuthTokens | null>(() =>
    getStoredTokens()
  );
  const [user, setUser] = useState<User | null>(() => deriveUser(getStoredTokens()));
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  // Track WHO is logged in so an identity change (different account, or
  // logout) drops every cached query — otherwise the previous account's
  // skills/media/boxes keep rendering from the react-query cache for the next
  // account until each query happens to refetch. Token refreshes for the SAME
  // user must not clear (the id doesn't change).
  const identityRef = useRef<string | null>(deriveUser(getStoredTokens())?.id ?? null);

  // Subscribe to token store changes — fires whenever apiClient refreshes,
  // logs in, logs out, or another tab logs in/out (storage event).
  useEffect(() => {
    const unsubscribe = subscribeToTokens((next) => {
      setTokensState(next);
      const nextUser = deriveUser(next);
      setUser(nextUser);
      const nextId = nextUser?.id ?? null;
      if (nextId !== identityRef.current) {
        identityRef.current = nextId;
        queryClient.clear();
      }
    });

    // Initial hydration. If we have valid stored tokens, derive the user.
    const initial = getStoredTokens();
    if (initial && !isTokenExpired(initial.accessToken)) {
      setTokensState(initial);
      setUser(deriveUser(initial));
      setIsLoading(false);
    } else if (initial) {
      // Access token expired but the refresh token outlives it — try a refresh
      // instead of forcing the user back through login on every reload. The
      // subscriber above picks up the new tokens on success.
      apiClient
        .refreshTokens()
        .catch(() => apiClient.clearTokens())
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }

    return unsubscribe;
    // queryClient is a stable singleton (created once in App.tsx)
  }, [queryClient]);

  // Proactively refresh the access token ~90s before it expires. tRPC (fetch)
  // and OpenSeadragon tile loads never trigger the axios 401 interceptor, so
  // without this the token silently expires and every tile/data request 401s.
  // On success the token store updates, which re-runs this effect and schedules
  // the next refresh; on failure tokens are cleared and the user is sent to login.
  useEffect(() => {
    const accessToken = tokens?.accessToken;
    if (!accessToken) return;
    const exp = getTokenExpiration(accessToken);
    if (!exp) return;
    const delay = Math.max(5_000, exp - Date.now() - 90_000);
    const timeoutId = window.setTimeout(() => {
      apiClient.refreshTokens().catch((err) => {
        console.error('[auth] proactive token refresh failed:', err);
      });
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [tokens?.accessToken]);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const response = await apiClient.login(credentials);
      // setTokens flows through auth-token, which fires our subscriber and
      // updates `tokens` + `user` for us — no need to setUser manually here.
      apiClient.setTokens({
        accessToken: response.access_token,
        idToken: response.id_token,
        refreshToken: response.refresh_token,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiClient.logout();
    // Subscriber clears `tokens` and `user`.
  };

  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated: !!user && !!tokens && !isTokenExpired(tokens.accessToken),
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
